import { Injectable } from '@angular/core';
import { addSeconds, isValid, parse, parseISO, set, subSeconds } from 'date-fns';
import { format, toZonedTime } from 'date-fns-tz';

export const CUSTOM_DATE_FORMATS = {
  display: {
    dateA11yLabel: 'dd/MM/yyyy',
    dateInput: 'dd/MM/yyyy',
    monthYearA11yLabel: 'MMMM yyyy',
    monthYearLabel: 'MMM yyyy',
    timeInput: 'HH:mm',
    timeOptionLabel: 'HH:mm',
  },
  parse: {
    dateInput: ['dd/MM/yyyy', 'yyyy-MM-dd'],
    timeInput: 'HH:mm',
  },
};

export const API_DATE_TIME_FORMAT = 'yyyy-MM-dd HH:mm:ss';
export const API_DATE_TIME_FORMAT_WITH_MS = 'yyyy-MM-dd HH:mm:ss.SSS';
export const API_DATE_FORMAT = 'yyyy-MM-dd';
export const API_TIME_FORMAT = 'HH:mm:ss';
export const API_MONTH_FORMAT = 'yyyy-MM';
export const DATE_FORMAT = 'dd.MM.yyyy';
export const TIME_FORMAT = 'HH:mm';
export const DATE_TIME_FORMAT = `${DATE_FORMAT} ${TIME_FORMAT}`;
export const DISPLAY_DATE_FORMAT = 'd MMM yyyy';
export const DISPLAY_TIME_FORMAT = 'HH:mm';
export const DISPLAY_TIME_FORMAT_WITH_SECONDS = 'HH:mm:ss';
export const DISPLAY_DATE_TIME_FORMAT = `${DISPLAY_DATE_FORMAT} ${DISPLAY_TIME_FORMAT}`;

@Injectable({
  providedIn: 'root',
})
export class DateUtilitiesService {
  public formatDate(date: Date | string | null, formatStr: string = DATE_FORMAT): string {
    if (!date || !isValid(date)) {
      return '';
    }

    const dateObj = typeof date === 'string' ? new Date(date) : date;

    return format(dateObj, formatStr);
  }

  public formatTime(date: Date | string | null, formatStr: string = TIME_FORMAT): string {
    if (!date || !isValid(date)) {
      return '';
    }

    const dateObj = typeof date === 'string' ? new Date(date) : date;

    return format(dateObj, formatStr);
  }

  public removeHoursAndRest(date: Date): Date {
    return set(date, { hours: 0, milliseconds: 0, minutes: 0, seconds: 0 });
  }

  public removeSecondsAndRest(date: Date): Date {
    return set(date, { milliseconds: 0, seconds: 0 });
  }

  public parseDate(dateStr: string, formatStr: string = DATE_FORMAT): Date {
    if (!dateStr) {
      return new Date();
    }

    if (dateStr.includes('T') || dateStr.includes('Z')) {
      return parseISO(dateStr);
    }

    return parse(dateStr, formatStr, new Date());
  }

  public parseDateTimeFromApi(dateStr: string): Date {
    if (!dateStr) {
      return new Date();
    }

    if (!dateStr.includes('Z')) {
      return toZonedTime(`${dateStr}Z`, Intl.DateTimeFormat().resolvedOptions().timeZone);
    }

    return toZonedTime(dateStr, Intl.DateTimeFormat().resolvedOptions().timeZone);
  }

  public parseTimeFromApi(dateStr: string | null, timeStr: string | null): Date | null {
    if (!dateStr) {
      return null;
    }

    if (!timeStr) {
      return toZonedTime(dateStr, Intl.DateTimeFormat().resolvedOptions().timeZone);
    }

    return toZonedTime(`${dateStr}T${timeStr}Z`, Intl.DateTimeFormat().resolvedOptions().timeZone);
  }

  public combineDateTime(date: Date, time?: Date | string | null, useSecondsAndMilliseconds = false): Date {
    if (!time) {
      return date;
    }

    if (typeof time === 'string') {
      const timeParts = time.split(':');
      const hours = parseInt(timeParts[0], 10);
      const minutes = parseInt(timeParts[1], 10);
      const seconds = timeParts.length > 2 ? parseInt(timeParts[2], 10) : 0;

      const result = set(new Date(date), {
        hours,
        minutes,
        seconds: useSecondsAndMilliseconds ? seconds : 0,

        milliseconds: 0,
      });

      return result;
    }

    const result = set(new Date(date), {
      hours: time.getHours(),
      minutes: time.getMinutes(),
      seconds: useSecondsAndMilliseconds ? time.getSeconds() : 0,

      milliseconds: useSecondsAndMilliseconds ? time.getMilliseconds() : 0,
    });

    return result;
  }

  public formatToApiDateTime(date: Date, keepDayValue = false): string | null {
    const timezoneOffset = date.getTimezoneOffset();
    let clonedDate = new Date(date.getTime());

    if (keepDayValue) {
      const isPlusOffset = timezoneOffset > 0;

      if (isPlusOffset) {
        clonedDate = subSeconds(date, 1);
      } else {
        clonedDate = addSeconds(date, 1);
      }
    }

    const utcDate = toZonedTime(clonedDate, 'UTC');

    if (!isValid(utcDate)) {
      return null;
    }

    return format(utcDate, API_DATE_TIME_FORMAT);
  }

  public formatToApiDate(date: Date): string {
    const utcDate = toZonedTime(date, 'UTC');

    if (!isValid(utcDate)) {
      return '';
    }

    return format(utcDate, API_DATE_FORMAT);
  }

  public formatToApiTime(time: Date): string {
    const utcTime = toZonedTime(time, 'UTC');

    if (!isValid(utcTime)) {
      return '';
    }

    return format(utcTime, API_TIME_FORMAT);
  }

  public formatToISO(date: Date): string {
    return date.toISOString();
  }

  public formatToISOSeconds(date: Date): string {
    return date.toISOString().replace(/\.\d+Z$/, 'Z');
  }

  public formatToLocalDateTime(isoString: string): string {
    const date = parseISO(isoString);

    return format(date, DATE_TIME_FORMAT);
  }

  public formatToDisplayDateTime(date: Date | string): string {
    const dateObj = typeof date === 'string' ? parseISO(date) : date;

    return format(dateObj, DISPLAY_DATE_TIME_FORMAT);
  }

  public formatToHHMMSS(date: Date | string): string {
    const dateObj = typeof date === 'string' ? parseISO(date) : date;

    return format(dateObj, DISPLAY_TIME_FORMAT_WITH_SECONDS);
  }
}
